import { motion } from "framer-motion";
import {
  Target,
  Radar,
  FileText,
  Sparkles,
  Building2,
  ArrowUpRight,
  DollarSign,
} from "lucide-react";
import MockFrame from "./MockFrame";

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: 0.08 + i * 0.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

/**
 * Tier palette for the fit ranking. Carries both a letter and a colour so the
 * ranking stays legible for colourblind viewers (mirrors the product's own
 * colourblind-safe A/B/C tiers).
 */
const TIER = {
  A: { dot: "#34d399", bg: "rgba(52, 211, 153, 0.12)", border: "rgba(52, 211, 153, 0.32)", text: "rgba(167, 243, 208, 0.95)" },
  B: { dot: "#60a5fa", bg: "rgba(96, 165, 250, 0.12)", border: "rgba(96, 165, 250, 0.3)", text: "rgba(191, 219, 254, 0.95)" },
  C: { dot: "#fbbf24", bg: "rgba(251, 191, 36, 0.10)", border: "rgba(251, 191, 36, 0.28)", text: "rgba(253, 230, 138, 0.95)" },
};

const leads = [
  { org: "Example Regional Health", meta: "6 facilities · aging on-prem", fit: 92, tier: "A", signals: 3 },
  { org: "Cascade Regional", meta: "3 facilities · IT roles posted", fit: 84, tier: "A", signals: 2 },
  { org: "Northlake Care Group", meta: "5 facilities · displacement signal", fit: 71, tier: "B", signals: 1 },
  { org: "Summit Valley Clinics", meta: "2 facilities · open RFP", fit: 58, tier: "C", signals: 1 },
];

function Stat({ icon: Icon, label, value, sub, accent, tone = "default", i }) {
  const valueColor =
    tone === "good" ? "#34d399" : tone === "warn" ? "#fbbf24" : accent.light;
  return (
    <motion.div
      variants={fadeUp}
      custom={i}
      className="rounded-xl border border-white/5 bg-white/[0.025] p-3"
    >
      <div className="flex items-center justify-between">
        <span
          className="flex h-7 w-7 items-center justify-center rounded-lg ring-1"
          style={{ backgroundColor: accent.bg, color: accent.primary, borderColor: accent.border }}
        >
          <Icon className="h-3.5 w-3.5" strokeWidth={2.4} />
        </span>
        <span
          className="text-[9px] font-bold uppercase tracking-[0.1em]"
          style={{ color: valueColor }}
        >
          {sub}
        </span>
      </div>
      <p className="mt-2.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-white/40">
        {label}
      </p>
      <p className="mt-0.5 text-[1.05rem] font-bold leading-none text-white tabular-nums">
        {value}
      </p>
    </motion.div>
  );
}

/** Horizontal fit-score meter on a stable 0–100 scale. */
function FitMeter({ pct, accent }) {
  return (
    <div className="flex items-center gap-2">
      <div className="relative h-1.5 w-[52px] overflow-hidden rounded-full bg-white/10 sm:w-16">
        <motion.span
          className="absolute inset-y-0 left-0 rounded-full"
          initial={{ width: 0 }}
          whileInView={{ width: `${pct}%` }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          style={{
            background: `linear-gradient(90deg, ${accent.primary}, ${accent.light})`,
            boxShadow: `0 0 8px ${accent.glow}`,
          }}
        />
      </div>
      <span
        className="text-[11px] font-bold tabular-nums"
        style={{ color: accent.light }}
      >
        {pct}
      </span>
    </div>
  );
}

export default function VeloCaptureVisual({ accent }) {
  return (
    <MockFrame
      domain="app.velocapture.com / leads"
      accent={accent}
      tabs={["Leads", "Bids", "Shortlist", "Evidence"]}
      activeTab={0}
    >
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
        className="space-y-3"
      >
        <div className="grid grid-cols-3 gap-2.5">
          <Stat
            icon={Target}
            label="Tier A matches"
            value="38"
            sub="This week"
            accent={accent}
            i={0}
          />
          <Stat
            icon={Radar}
            label="Live buying signals"
            value="124"
            sub="↑ 21%"
            tone="good"
            accent={accent}
            i={1}
          />
          <Stat
            icon={FileText}
            label="Open bids matched"
            value="12"
            sub="In category"
            accent={accent}
            i={2}
          />
        </div>

        <motion.div
          variants={fadeUp}
          custom={3}
          className="rounded-xl border border-white/5 bg-white/[0.025]"
        >
          <div className="flex items-center justify-between border-b border-white/5 px-3 py-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">
              Ranked shortlist
            </p>
            <span
              className="inline-flex items-center gap-1 text-[9.5px] font-bold"
              style={{ color: accent.light }}
            >
              <Sparkles className="h-3 w-3" />
              Ranked by fit
            </span>
          </div>
          <ul className="divide-y divide-white/5">
            {leads.map((l, i) => {
              const t = TIER[l.tier];
              return (
                <motion.li
                  key={l.org}
                  variants={fadeUp}
                  custom={4 + i}
                  className="flex items-center gap-2.5 px-3 py-2"
                >
                  <span
                    className="inline-flex items-center gap-1 rounded-md border px-1.5 py-[2px] text-[9.5px] font-bold"
                    style={{
                      borderColor: t.border,
                      backgroundColor: t.bg,
                      color: t.text,
                    }}
                  >
                    <span
                      className="h-1 w-1 rounded-full"
                      style={{ backgroundColor: t.dot }}
                    />
                    {l.tier}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 truncate text-[11px] font-semibold text-white/90">
                      <Building2
                        className="h-2.5 w-2.5 shrink-0"
                        style={{ color: accent.primary }}
                      />
                      {l.org}
                    </p>
                    <p className="flex items-center gap-1 truncate text-[9.5px] text-white/45">
                      <Radar className="h-2.5 w-2.5" style={{ color: accent.primary }} />
                      {l.meta} · {l.signals} live
                    </p>
                  </div>
                  <FitMeter pct={l.fit} accent={accent} />
                </motion.li>
              );
            })}
          </ul>
        </motion.div>

        <motion.div
          variants={fadeUp}
          custom={9}
          className="grid grid-cols-1 gap-2.5 sm:grid-cols-5"
        >
          <div
            className="rounded-xl border p-3 sm:col-span-3"
            style={{ backgroundColor: accent.bg, borderColor: accent.border }}
          >
            <div className="flex items-center justify-between">
              <p
                className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em]"
                style={{ color: accent.light }}
              >
                <Sparkles className="h-3 w-3" />
                Why this match
              </p>
              <span
                className="text-[9.5px] font-bold tabular-nums"
                style={{ color: accent.light }}
              >
                92 fit
              </span>
            </div>
            <p className="mt-2 text-[10.5px] leading-snug text-white/75">
              Six facilities on aging on-prem systems, three IT roles posted
              this quarter, and an open RFP in your category.{" "}
              <span style={{ color: accent.light }} className="font-semibold">
                Evidence linked to every signal.
              </span>
            </p>
          </div>

          <div className="flex flex-col justify-between rounded-xl border border-white/5 bg-white/[0.025] p-3 sm:col-span-2">
            <div className="flex items-center justify-between">
              <span
                className="flex h-7 w-7 items-center justify-center rounded-lg ring-1"
                style={{ backgroundColor: accent.bg, color: accent.primary, borderColor: accent.border }}
              >
                <DollarSign className="h-3.5 w-3.5" strokeWidth={2.4} />
              </span>
              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-[#34d399]">
                No list-building
              </span>
            </div>
            <div className="mt-2.5">
              <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-white/40">
                Data cost per lead
              </p>
              <p className="mt-0.5 flex items-end gap-1 text-[1.05rem] font-bold leading-none text-white tabular-nums">
                $0
                <span
                  className="mb-[1px] inline-flex items-center text-[9px] font-bold"
                  style={{ color: accent.light }}
                >
                  <ArrowUpRight className="h-2.5 w-2.5" />
                  public + licensed
                </span>
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </MockFrame>
  );
}
